import React, { useState } from "react";
import { fabric } from "fabric";
import { Icon, Button } from "@blueprintjs/core";
import { SketchPicker } from "react-color";
import reactCSS from "reactcss";
import { ColorPickerPencil } from "../Toolbar/ColorPickerPencil";

export function BackgroundComponet(editor) {
  const { Canvaseditor, setShowToolbar, closeTab } = editor;
  const [bgColorPicker, setbgcolorPicker] = useState({
    displayColorPicker: false,
    color: {
      r: "255",
      g: "255",
      b: "255",
      a: "100",
    },
  });
  // Notify undo/redo that background changed (so it can push current state to history).
  // Use both canvas.fire (Fabric Observable) and window event so it works regardless of Fabric API.
  const notifyBackgroundChanged = () => {
    if (!Canvaseditor) return;
    if (typeof Canvaseditor.fire === "function") {
      Canvaseditor.fire("background:changed");
    }
    if (typeof window !== "undefined" && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent("canvas:background:changed"));
    }
  };

  // Upload BG Image.
  const uploadBGImage = (e) => {
    Canvaseditor.setBackgroundColor(
      "",
      Canvaseditor.renderAll.bind(Canvaseditor)
    );
    Canvaseditor.setBackgroundImage(
      0,
      Canvaseditor.renderAll.bind(Canvaseditor)
    );
    var file = e.target.files[0];
    var fsize = Math.round(file.size / 1024);
    if (fsize > 2048) {
      alert("File too big, please select a file less than 2mb");
      return;
    }
    var reader = new FileReader();
    reader.onload = function (f) {
      var data = f.target.result;
      fabric.Image.fromURL(data, function (img) {
        setShowToolbar(true);

        img.set({
          originX: "left",
          scaleX: Canvaseditor.getWidth() / img.width, //new update
          scaleY: Canvaseditor.getHeight() / img.height, //new update
          originY: "top",
        });
        Canvaseditor.setBackgroundImage(img, () => {
          Canvaseditor.renderAll();
          notifyBackgroundChanged();
        });
      });
    };
    reader.readAsDataURL(file);

    // Close tab event.
    const resizeWidth = window.innerWidth;
    if (resizeWidth <= 770) {
      closeTab();
    }
  };

  // Remove BG Image.
  const removeBGImage = () => {
    Canvaseditor.setBackgroundColor("", () => {
      Canvaseditor.setBackgroundImage(0, () => {
        Canvaseditor.renderAll();
        notifyBackgroundChanged();
      });
    });
    // Close tab event.
    const resizeWidth = window.innerWidth;
    if (resizeWidth <= 770) {
      closeTab();
    }
  };

  // Solid BG Color.
  const solidBGColor = (rgb, r, g, b) => {
    setShowToolbar(true);
    Canvaseditor.setBackgroundColor(rgb, () => {
      Canvaseditor.setBackgroundImage(0, () => {
        Canvaseditor.renderAll();
        notifyBackgroundChanged();
      });
    });
    // Close tab event.
    const resizeWidth = window.innerWidth;
    if (resizeWidth <= 770) {
      closeTab();
    }
  };

  // Color Picker.
  const handleBgColorClick = () => {
    setbgcolorPicker({
      displayColorPicker: !bgColorPicker.displayColorPicker,
      color: {
        r: "255",
        g: "255",
        b: "255",
        a: "100",
      },
    });
  };

  const handleBgColorClose = () => {
    setbgcolorPicker({ displayColorPicker: false, color: bgColorPicker.color });
  };

  const handleBgColorChange = (color) => {
    setShowToolbar(true);
    setbgcolorPicker({ displayColorPicker: true, color: color.rgb });
    const getBG = `rgb(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b})`;
    Canvaseditor.setBackgroundColor(getBG, () => {
      Canvaseditor.setBackgroundImage(0, () => {
        Canvaseditor.renderAll();
        notifyBackgroundChanged();
      });
    });
  };

  // Apply HEX color picked from the eyedropper to the background color picker
  // state and propagate it to the canvas background.
  const applyHexToBackground = (hex) => {
    if (!hex || !Canvaseditor) return;

    const value = hex.trim();
    if (!value.startsWith("#") || (value.length !== 7 && value.length !== 4)) {
      return;
    }

    const expandShortHex = (v) => {
      if (v.length !== 4) return v;
      const r = v[1];
      const g = v[2];
      const b = v[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    };

    const normalized = expandShortHex(value);
    const r = parseInt(normalized.slice(1, 3), 16);
    const g = parseInt(normalized.slice(3, 5), 16);
    const b = parseInt(normalized.slice(5, 7), 16);

    const nextColor = {
      r,
      g,
      b,
      a: bgColorPicker.color.a || 100,
    };

    setbgcolorPicker({
      displayColorPicker: bgColorPicker.displayColorPicker,
      color: nextColor,
    });

    const getBG = `rgb(${r}, ${g}, ${b})`;

    setShowToolbar(true);
    Canvaseditor.setBackgroundColor(getBG, () => {
      Canvaseditor.setBackgroundImage(0, () => {
        Canvaseditor.renderAll();
        notifyBackgroundChanged();
      });
    });
  };

  const handleBgEyedropperPicked = (hex) => {
    applyHexToBackground(hex);
  };

  const bgRgb = bgColorPicker.color || { r: 255, g: 255, b: 255, a: 100 };
  const bgRgbToHex = (r, g, b) =>
    "#" + [r, g, b].map((x) => Math.round(Number(x)).toString(16).padStart(2, "0")).join("");
  const bgHexValue = bgRgbToHex(Number(bgRgb.r), Number(bgRgb.g), Number(bgRgb.b)).replace("#", "").toUpperCase();

  const handleBgCustomHexChange = (e) => {
    const v = e.target.value.replace(/[^0-9A-Fa-f]/g, "").slice(0, 6);
    if (v.length === 6) {
      const nextRgb = { r: parseInt(v.slice(0, 2), 16), g: parseInt(v.slice(2, 4), 16), b: parseInt(v.slice(4, 6), 16), a: Number(bgRgb.a) || 100 };
      setbgcolorPicker({ displayColorPicker: bgColorPicker.displayColorPicker, color: nextRgb });
      handleBgColorChange({ rgb: nextRgb });
    }
  };

  const handleBgCustomRgbaChange = (key, val) => {
    const n = key === "a" ? Math.min(100, Math.max(0, parseInt(val, 10) || 0)) : Math.min(255, Math.max(0, parseInt(val, 10) || 0));
    const nextRgb = { ...bgRgb, [key]: key === "a" ? n : n };
    setbgcolorPicker({ displayColorPicker: bgColorPicker.displayColorPicker, color: nextRgb });
    handleBgColorChange({ rgb: nextRgb });
  };

  // Set css for color picker.
  const bgStyles = reactCSS({
    default: {
      color: {
        width: "36px",
        height: "14px",
        borderRadius: "2px",
        // background: `rgba(${bgColorPicker.color.r}, ${bgColorPicker.color.g}, ${bgColorPicker.color.b}, ${bgColorPicker.color.a})`,
        background: `rgb(${bgColorPicker.color.r}, ${bgColorPicker.color.g}, ${bgColorPicker.color.b})`,
      },
      swatch: {
        display: "inline-block",
        // background: `rgba(${bgColorPicker.color.r}, ${bgColorPicker.color.g}, ${bgColorPicker.color.b}, ${bgColorPicker.color.a})`,
        background: `rgb(${bgColorPicker.color.r}, ${bgColorPicker.color.g}, ${bgColorPicker.color.b})`,
        width: "41px",
        height: "41px",
        borderRadius: "2px",
        boxShadow: "0 0 2px rgb(16 22 26 / 30%)",
        cursor: "pointer",
      },
      popover: {
        position: "absolute",
        zIndex: "2",
      },
      cover: {
        position: "absolute",
        top: "0px",
        right: "0px",
        bottom: "0px",
        left: "0px",
      },
    },
  });

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ marginBottom: "30px" }}>
        <label
          htmlFor="bg-input-file"
          className="bp4-button"
          style={{ width: "45%", marginRight: "10px" }}
        >
          <Icon size="18" icon="upload" />
          <span className="bp4-button-text">Upload BG</span>
        </label>
        <Button
          id="removeBG"
          text="Remove BG"
          onClick={removeBGImage}
          icon={<Icon icon="graph-remove" />}
          style={{ width: "45%" }}
        />
        <input
          onChange={uploadBGImage}
          type="file"
          id="bg-input-file"
          style={{ display: "none" }}
        />
      </div>
      <div style={{ marginBottom: "20px" }}>
        <h4>Solid Colors </h4>
        <div
          style={{
            display: "flex",
            paddingBottom: "10px",
            justifyContent: "flex-start",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(255, 255, 255)", 255, 255, 255)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(255, 255, 255)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(82, 113, 255)", 82, 113, 255)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(82, 113, 255)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(255, 145, 77)", 255, 145, 77)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(255, 145, 77)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(126, 217, 87)", 126, 217, 87)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(126, 217, 87)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(255, 222, 89)", 255, 222, 89)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(255, 222, 89)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(203, 108, 230)", 203, 108, 230)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(203, 108, 230)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(208, 2, 27)", 208, 2, 27)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(208, 2, 27)",
              }}
            ></div>
          </div>

          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(245, 166, 35)", 245, 166, 35)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(245, 166, 35)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(248, 231, 28)", 248, 231, 28)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(248, 231, 28)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(139, 87, 42)", 139, 87, 42)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(139, 87, 42)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(184, 233, 134)", 184, 233, 134)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(184, 233, 134)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(65, 117, 5)", 65, 117, 5)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(65, 117, 5)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(74, 144, 226)", 74, 144, 226)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(74, 144, 226)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(80, 227, 202)", 80, 227, 202)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(80, 227, 202)",
              }}
            ></div>
          </div>
          <div className="solid-color">
            <div
              onClick={() => solidBGColor("rgb(0, 0, 0)", 0, 0, 0)}
              style={{
                width: "100%",
                height: "100%",
                background: "rgb(0, 0, 0)",
              }}
            ></div>
          </div>
        </div>
        <div className="font-color-container">
          <div>
            <div style={bgStyles.swatch} onClick={handleBgColorClick}>
              <Icon
                icon="color-fill"
                style={{ marginLeft: "10px", marginTop: "10px" }}
              />
              <div style={bgStyles.color} />
            </div>
            {bgColorPicker.displayColorPicker ? (
              <div style={bgStyles.popover}>
                <div style={bgStyles.cover} onClick={handleBgColorClose} />
                <div className="color-picker-fields-wrap" style={{ position: "relative" }}>
                  <SketchPicker
                    color={bgColorPicker.color}
                    onChange={handleBgColorChange}
                  />
                  <div className="color-picker-fields-inner">
                    <div className="color-picker-hex-row">
                      <span className="color-picker-hex-label">HEX</span>
                      <input
                        type="text"
                        className="color-picker-hex-input"
                        value={bgHexValue}
                        onChange={handleBgCustomHexChange}
                        onBlur={(e) => {
                          const v = e.target.value.replace(/[^0-9A-Fa-f]/g, "").padEnd(6, "0").slice(0, 6);
                          if (v.length === 6) {
                            const nextRgb = { r: parseInt(v.slice(0, 2), 16), g: parseInt(v.slice(2, 4), 16), b: parseInt(v.slice(4, 6), 16), a: Number(bgRgb.a) || 100 };
                            setbgcolorPicker({ displayColorPicker: bgColorPicker.displayColorPicker, color: nextRgb });
                            handleBgColorChange({ rgb: nextRgb });
                          }
                        }}
                      />
                      <ColorPickerPencil Canvaseditor={Canvaseditor} mode="fill" applyToActiveObject={false} onColorPicked={handleBgEyedropperPicked} />
                    </div>
                    <div className="color-picker-rgba-row">
                      {["r", "g", "b", "a"].map((k) => (
                        <div key={k} style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 10, color: "#666", textAlign: "center", marginBottom: 2, textTransform: "uppercase" }}>{k}</div>
                          <input
                            type="number"
                            min={0}
                            max={k === "a" ? 100 : 255}
                            value={k === "a" ? Math.round(Number(bgRgb.a) || 100) : (Number(bgRgb[k]) || 0)}
                            onChange={(e) => handleBgCustomRgbaChange(k, e.target.value)}
                            style={{ width: "100%", padding: "4px", border: "1px solid #ccc", borderRadius: 3, fontSize: 11, boxSizing: "border-box" }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
